import { AsyncPipe, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { combineLatestWith, filter, first, map, Observable, shareReplay, Subject } from 'rxjs';

import { select } from '@ngneat/elf';
import { ConfigRepository } from '../../../../../shared/repositories/config.repository';
import { BreakpointService } from '../../../../../shared/services/breakpoint.service';
import { DeliveryType } from '../../../../../api/v1/models/delivery-type';
import { SessionRepository } from '../../../../../shared/repositories/session.repository';
import { AddressDto } from '../../../../../api/v1/models/address-dto';
import { OrderDeliveryModalService } from '../../../../../components/order-delivery-modal/order-delivery-modal.service';
import { AddressDetails, Outlet, OutletMode, WorkingHours } from 'src/app/api/v1/models';
import { zeroMaskFromTimeInterval } from 'src/app/shared/utils/get-time-intervals';
import { WorkingTimeModalService } from '../working-time-modal/working-time-modal.service';
import { ObjectKeyArray } from 'src/app/shared/interfaces/object-key-array.interface';

@Component({
  selector: 'app-rest-info',
  templateUrl: './rest-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    AsyncPipe,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    NgTemplateOutlet,
    MatButtonModule,
  ]
})
export class RestInfoComponent {
  constructor(
    private configRepository: ConfigRepository,
    private sessionRepository: SessionRepository,
    private orderDeliveryModal: OrderDeliveryModalService,
    public breakPoint: BreakpointService,
    private workingTimeModalService: WorkingTimeModalService
  ) {
    this.openChooseAddressModalSubj$.asObservable().pipe(
      combineLatestWith(
        this.configRepository.outletMode$.pipe(first()),
        this.configRepository.isProvidingRestoSelection$
      ),
      filter(([_, outletMode]) => !!outletMode && outletMode != OutletMode.Eatin),
      filter(([_, outletMode, isChain]) => outletMode === OutletMode.Qr ? isChain : true)
    ).subscribe(() => {
      this.orderDeliveryModal.setOrderTypeModalState(true);
    })
  }

  private openChooseAddressModalSubj$: Subject<void> = new Subject<void>();

  deliveryTypes = DeliveryType;

  selectedDeliveryType$: Observable<DeliveryType | null> = this.sessionRepository.selectedDeliveryType$;
  deliveryAddress$: Observable<AddressDto | null> = this.sessionRepository.deliveryAddress$;

  address$: Observable<string | null> = this.configRepository.address$.pipe(
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  noOutlets$: Observable<boolean> = this.configRepository.outlets$.pipe(select(outlets => !outlets.length));

  activeOutlet$: Observable<Outlet> = this.configRepository.activeOutlet$;

  addressDetails$ = this.configRepository.addressDetails$.pipe(
    combineLatestWith(this.address$),
    map(([addressDetails, address]) =>
      !!addressDetails ? `${addressDetails.streetLine}, ${addressDetails.city}` : address
    ),
    shareReplay({ refCount: false, bufferSize: 1 })
  );

  deliveryAvailableIntervals$ = this.configRepository.deliveryAvailableIntervals$;

  onChooseAddress() {
    this.openChooseAddressModalSubj$.next();
  }

  onChooseWorkingHours() {
    this.workingTimeModalService.open();
  }

  getAddress(addressDetails: AddressDetails | null, address: string | null) {
    return !!addressDetails ? `${addressDetails.streetLine}, ${addressDetails.city}` : address;
  }

  getWorkingHours(
    outlet: Outlet | null,
    selectedDeliveryType: DeliveryType | null,
    deliveryAvailableIntervals: ObjectKeyArray<WorkingHours> | null
  ): string {
    const currentDayName: string = new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(new Date());
    let dayHours: WorkingHours[] | undefined;

    if (selectedDeliveryType === DeliveryType.Eatin) {
      dayHours = outlet?.eatinIntervals[currentDayName];
    } else if (selectedDeliveryType === DeliveryType.Pickup) {
      dayHours = outlet?.pickupIntervals[currentDayName];
    } else {
      dayHours = deliveryAvailableIntervals?.[currentDayName];
    }

    if (!dayHours) {
      return '';
    }

    let result = '';

    dayHours.forEach(interval => {
      const currentTime = new Date().getHours() * 60 + new Date().getMinutes();
      const openTime: number = interval.open.hour * 60 + interval.open.minute;
      let closeTime: number = interval.close.hour * 60 + interval.close.minute;

      if(closeTime < openTime) {
        closeTime += 24 * 60
      }

      if (currentTime >= openTime && currentTime <= closeTime) {
        result = zeroMaskFromTimeInterval(interval.open) + '-' + zeroMaskFromTimeInterval(interval.close);
      }
    })

    return result || $localize`:@@EmptyOutlesMessage:Restaurant is closed`;
  }
}
